import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import type { TeamOfficeGetResponse } from "@bezoned/shared/types/api/team/team-office-get.types.js";
import { useState } from "react";

import { TeamOfficeDesignSelector } from "../../../tabs/theme/components/team-office-design-selector.js";
import { TeamEditThemeConfigDialog } from "./team-edit-theme-config-dialog.js";
import { TeamEditThemeSwitchDialog } from "./team-edit-theme-switch-dialog.js";
import { useEditThemeState } from "./use-edit-theme-state.js";

interface TeamEditThemeTabProps {
  msTeamId: string;
  teamQueryData: TeamOfficeGetResponse | undefined;
  onChange: () => void;
  onError: (error: unknown) => void;
}

export const TeamEditThemeTab = ({
  msTeamId,
  teamQueryData,
  onChange,
  onError,
}: TeamEditThemeTabProps) => {
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
  const [switchTargetPresetId, setSwitchTargetPresetId] = useState<
    string | null
  >(null);

  const theme = useEditThemeState({
    msTeamId,
    onChange,
    onError,
    teamQueryData,
  });

  const switchTargetPreset = theme.officePresets.find(
    (preset) => preset.id === switchTargetPresetId
  );

  const handleConfigSaveSuccess = () => {
    setIsConfigDialogOpen(false);
    theme.refreshOfficeData();
  };

  const handleSwitchSave = () => {
    setSwitchTargetPresetId(null);
    theme.setPreviewedPresetId(null);
    theme.refreshOfficeData();
  };

  const handleSwitchDesignClick = () => {
    if (theme.effectivePreviewedId && !theme.isCurrentPreset) {
      setSwitchTargetPresetId(theme.effectivePreviewedId);
    }
  };
  const handleClose97 = useStableCallback(() => setSwitchTargetPresetId(null));

  const handleClose109 = useStableCallback(() => setIsConfigDialogOpen(false));
  const handlePresetSelect86 = useStableCallback((presetId) =>
    theme.setPreviewedPresetId(presetId)
  );

  return (
    <form className="flex w-full flex-col gap-6">
      <TeamOfficeDesignSelector
        action={{
          disabled: theme.editMutation.isPending || theme.isCurrentPreset,
          label: "Switch design",
          onClick: handleSwitchDesignClick,
          tooltip: "Select different office design to switch",
        }}
        configureAction={{
          disabled: theme.editMutation.isPending || !theme.isCurrentPreset,
          label: "Configure",
          onClick: () => setIsConfigDialogOpen(true),
          tooltip: "You can only configure the current office design",
        }}
        isDropdownDisabled={
          theme.editMutation.isPending || theme.presetsQuery.isLoading
        }
        onPresetSelect={handlePresetSelect86}
        presets={theme.officePresets}
        selectedPreset={theme.previewedPresetPreview}
        selectedPresetDisplayName={theme.previewedPresetName}
        selectedPresetId={theme.effectivePreviewedId}
      />

      {switchTargetPresetId !== null && (
        <TeamEditThemeSwitchDialog
          availableChannels={theme.channelsQuery.data?.channels ?? []}
          currentOfficeTables={theme.officeData?.tables ?? []}
          currentPresetTables={theme.officeData?.preset.manifest.tables ?? []}
          isChannelsLoading={theme.channelsQuery.isLoading}
          msTeamId={msTeamId}
          newPresetTables={switchTargetPreset?.manifest.tables ?? []}
          onClose={handleClose97}
          onSave={handleSwitchSave}
          open
          presetId={switchTargetPresetId}
        />
      )}

      <TeamEditThemeConfigDialog
        availableChannels={theme.channelsQuery.data?.channels ?? []}
        isLoading={theme.channelsQuery.isLoading || theme.officeQuery.isLoading}
        msTeamId={msTeamId}
        officeTables={theme.officeData?.tables ?? []}
        onClose={handleClose109}
        onOfficeUpdateSuccess={handleConfigSaveSuccess}
        open={isConfigDialogOpen}
        presetTables={theme.officeData?.preset.manifest.tables ?? []}
      />
    </form>
  );
};
